import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, type FormEvent } from "react";
import {
  Bell,
  CalendarPlus,
  Camera,
  ClipboardList,
  Crown,
  Home,
  KeyRound,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  MessageCircle,
  Palette,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getSessionInfo } from "@/lib/session.functions";
import {
  addProperty,
  deleteProperty,
  getMyProfile,
  requestAccountDeletion,
  updateMyProfile,
} from "@/lib/profile.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AchievementsPanel } from "@/components/AchievementsPanel";
import { SavedHomes } from "@/components/home/SavedHomes";
import { AvatarPicker } from "@/components/profile/AvatarPicker";
import { PublicBadgeGrid } from "@/components/achievements/PublicBadgeGrid";
import { Leaderboard } from "@/components/achievements/Leaderboard";
import { SocialFeed } from "@/components/social/SocialFeed";
import { AppHeader } from "@/components/site/AppHeader";
import { DisclosureCard } from "@/components/DisclosureCard";
import { AccountGrowthCards } from "@/components/account/AccountGrowthCards";
import { ThemeControl } from "@/components/site/ThemeProvider";

const sessionQuery = queryOptions({ queryKey: ["session-info"], queryFn: () => getSessionInfo() });
const profileQuery = queryOptions({ queryKey: ["my-profile"], queryFn: () => getMyProfile() });

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({
    meta: [
      { title: "My account — Shields" },
      { name: "description", content: "Your Shields workspace." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  async function handleSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  const quickActions = [
    { to: "/book", icon: CalendarPlus, label: "Book a service", show: true },
    { to: "/home", icon: Home, label: "My Home", show: true },
    { to: "/plans", icon: Crown, label: "Membership", show: true },
    { to: "/notifications", icon: Bell, label: "Notifications", show: true },
    { to: "/inbox", icon: Mail, label: "Inbox", show: true },
    { to: "/bookings", icon: ClipboardList, label: "My bookings", show: true },
    { to: "/messages", icon: MessageCircle, label: "Messages", show: true },
    { to: "/personalise", search: { experience: "customer" as const }, icon: Palette, label: "Personalise Shields", show: true },
  ].filter((a) => a.show);

  return (
    <div className="min-h-screen bg-background pb-28">
      <AppHeader workspace="customer" />
      <nav
        aria-label="Account shortcuts"
        className="sticky top-20 z-20 border-b border-border/70 bg-background/90 backdrop-blur-xl"
      >
        <div className="section-pad mx-auto flex max-w-3xl items-center gap-2 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ul className="flex items-center gap-2">
            {quickActions.map((a) => (
              <li key={a.to + a.label}>
                {"search" in a ? (
                  <Link to="/personalise" search={a.search} className="flex min-h-10 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-card px-3 text-xs font-bold text-muted-foreground transition hover:border-primary/30 hover:text-primary"><a.icon className="h-4 w-4" aria-hidden /> {a.label}</Link>
                ) : (
                  <Link to={a.to} className="flex min-h-10 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-card px-3 text-xs font-bold text-muted-foreground transition hover:border-primary/30 hover:text-primary"><a.icon className="h-4 w-4" aria-hidden /> {a.label}</Link>
                )}
              </li>
            ))}
          </ul>
          <button
            onClick={handleSignOut}
            className="flex min-h-10 shrink-0 items-center gap-2 rounded-full px-3 text-xs font-bold text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <LogOut className="h-4 w-4" aria-hidden /> Sign out
          </button>
        </div>
      </nav>

      <main className="product-page layout-profile section-pad mx-auto py-8 sm:py-12">
        <ProfileSection isPartner={false} />

        <AccountGrowthCards isPartner={false} />

        <div className="mt-8 space-y-4">
          <DisclosureCard title="Appearance" summary="Light, Dark or your device setting."><ThemeControl /></DisclosureCard>
          <DisclosureCard title="Posts & community" summary="Your posts, public badges and community leaderboard.">
            <SocialFeed heading="" authorId={session.userId} />
            <div className="mt-6 space-y-6">
              <PublicBadgeGrid
                userId={session.userId}
                audience="customer"
                heading="Your Customer achievements"
              />
              <Leaderboard audience="customer" />
            </div>
          </DisclosureCard>

          <DisclosureCard title="Customer achievements" summary="Profile, booking and review milestones—no Shields Credits needed.">
            <AchievementsPanel audience="customer" />
            
          </DisclosureCard>

          <DisclosureCard title="Saved properties" summary="Homes and addresses you use when requesting work.">
            <SavedHomes />
            <PropertiesSection />
          </DisclosureCard>

          <DisclosureCard title="Security & data" summary="Password, privacy, export and account-deletion controls.">
            <h2 className="flex items-center gap-2 font-display font-bold">
              <KeyRound className="h-5 w-5 text-primary" aria-hidden /> Security &amp; data
            </h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/reset-password" className="font-semibold text-primary underline">
                  Change your password
                </Link>
              </li>
              <li>Your records are never visible to other customers.</li>
              <li>To export your data, contact support from the Help page.</li>
            </ul>
            <DeleteAccountSection />
          </DisclosureCard>
        </div>
      </main>
    </div>
  );
}

function DeleteAccountSection() {
  const requestDeletion = useServerFn(requestAccountDeletion);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [requested, setRequested] = useState(false);

  async function handleDelete() {
    setSending(true);
    const res = await requestDeletion({ data: { confirm: true } });
    setSending(false);
    if (!res.ok) {
      toast.error(res.error ?? "Could not start account deletion");
      setConfirming(false);
      return;
    }
    setRequested(true);
    setConfirming(false);
    toast.success("Deletion requested — we'll take it from here");
  }

  if (requested) {
    return (
      <p className="mt-4 rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground">
        Your deletion request is in. Your account and data will be removed once any open items are
        settled — you'll get a confirmation here.
      </p>
    );
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      {confirming ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold">
            Delete your account? This removes your profile, bookings history and credits, and can't
            be undone.
          </p>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              className="min-h-11"
              onClick={handleDelete}
              disabled={sending}
            >
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Yes, delete my account
            </Button>
            <Button variant="outline" className="min-h-11" onClick={() => setConfirming(false)}>
              Keep my account
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="ghost"
          className="min-h-11 text-destructive hover:text-destructive"
          onClick={() => setConfirming(true)}
        >
          <Trash2 className="mr-2 h-4 w-4" aria-hidden /> Delete my account
        </Button>
      )}
    </div>
  );
}

function ProfileSection({ isPartner }: { isPartner: boolean }) {
  const { data } = useSuspenseQuery(profileQuery);
  const queryClient = useQueryClient();
  const save = useServerFn(updateMyProfile);
  const [fullName, setFullName] = useState(data.profile?.full_name ?? "");
  const [phone, setPhone] = useState(data.profile?.phone ?? "");
  const [bio, setBio] = useState(data.profile?.bio ?? "");
  const [username, setUsername] = useState(data.profile?.username ?? "");
  const [saving, setSaving] = useState(false);
  const [bannerBusy, setBannerBusy] = useState(false);

  async function saveAvatar(path: string) {
    const res = await save({ data: { fullName: fullName || "Shields member", avatarPath: path } });
    if (!res.ok) {
      toast.error(res.error ?? "Could not save your photo");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
    toast.success("Photo updated");
  }

  async function saveBanner(file: File) {
    setBannerBusy(true);
    const { uploadPhoto } = await import("@/lib/uploads");
    let path: string;
    try {
      path = await uploadPhoto("avatars", file);
    } catch (err) {
      setBannerBusy(false);
      toast.error(err instanceof Error ? err.message : "Could not upload that image");
      return;
    }
    const res = await save({
      data: {
        fullName,
        phone: phone || null,
        bio: bio || null,
        username: username.trim() || null,
        bannerPath: path,
      },
    });
    setBannerBusy(false);
    if (!res.ok) {
      toast.error(res.error ?? "Could not save your cover photo");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
    toast.success("Cover photo updated");
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    const res = await save({
      data: {
        fullName,
        phone: phone || null,
        bio: bio || null,
        username: username.trim() || null,
      },
    });
    setSaving(false);
    if (!res.ok) {
      toast.error(res.error ?? "Could not save your profile");
      return;
    }
    toast.success("Profile saved");
    await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
  }

  const completedProfileItems = [
    fullName.trim().length >= 2,
    username.trim().length >= 3,
    Boolean(data.avatarUrl),
    bio.trim().length > 0,
  ].filter(Boolean).length;

  return (
    <section className="card-surface mt-2 overflow-hidden">
      {/* Cover photo — the frame shows exactly what appears on your profile */}
      <div className="group relative h-32 bg-[image:var(--gradient-premium)] sm:h-44">
        {data.bannerUrl && (
          <img src={data.bannerUrl} alt="" className="h-full w-full object-cover" />
        )}
        <div
          className="pointer-events-none absolute inset-2 rounded-xl border border-dashed border-white/45 opacity-0 transition group-hover:opacity-100"
          aria-hidden
        />
        <label className="absolute right-2 top-2 flex cursor-pointer items-center gap-1.5 rounded-full bg-background/85 px-2.5 py-1 text-xs font-bold backdrop-blur transition hover:bg-background">
          <Camera className="h-3.5 w-3.5" aria-hidden />
          {bannerBusy ? "Uploading…" : "Cover"}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) saveBanner(f);
            }}
          />
        </label>
      </div>

      <div className="px-5 pb-5 sm:px-7 sm:pb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="relative -mt-10 rounded-full ring-4 ring-card">
              <AvatarPicker
                url={data.avatarUrl}
                name={fullName || "Shields member"}
                onUploaded={saveAvatar}
              />
              <span
                className="pointer-events-none absolute inset-0 rounded-full border-2 border-dashed border-primary/40"
                aria-hidden
              />
            </div>
            <div className="min-w-0 pt-2">
              <h1 className="font-display text-xl font-extrabold leading-tight sm:text-2xl">
                {fullName || "Your profile"}
              </h1>
              <p className="text-sm font-semibold text-primary">@{username || "pick-a-username"}</p>
              <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
                {isPartner ? "Verified Shields partner" : "Shields member"}
              </p>
            </div>
          </div>
          {username && (
            <Link
              to="/u/$username"
              params={{ username }}
              className="mt-2 rounded-full border border-border px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10"
            >
              View public profile
            </Link>
          )}
        </div>

        {bio && <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{bio}</p>}

        <dl className="mt-4 grid grid-cols-3 gap-2 border-y border-border py-3 text-center sm:grid-cols-6">
          {[
            { k: "Followers", v: data.social.followers },
            { k: "Following", v: data.social.following },
            { k: "Posts", v: data.social.posts },
            isPartner
              ? { k: "Jobs done", v: data.stats.bookings }
              : { k: "Jobs booked", v: data.stats.bookings },
            { k: "Reviews", v: data.stats.reviews },
            isPartner
              ? { k: "Credentials", v: data.stats.trophies }
              : { k: "Trophies", v: data.stats.trophies },
          ].map((s) => (
            <div key={s.k}>
              <dt className="text-[11px] font-semibold text-muted-foreground">{s.k}</dt>
              <dd className="font-display text-lg font-extrabold">{s.v}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 text-xs text-muted-foreground">
          {isPartner
            ? "Customers see your trading name, work photos, ratings and typical prices."
            : "Partners see your name, photo and username only — never your contact details."}
        </p>
      </div>

      <details className="group border-t border-border">
        <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between px-6 py-4 text-sm font-bold text-primary [&::-webkit-details-marker]:hidden">
          <span>
            <span className="block">Edit your profile</span>
            <span className="block text-xs font-semibold text-muted-foreground">
              {completedProfileItems} of 4 profile steps complete
            </span>
          </span>
          <span className="text-xs font-semibold text-muted-foreground group-open:hidden">Open</span>
          <span className="hidden text-xs font-semibold text-muted-foreground group-open:inline">Close</span>
        </summary>
        <form onSubmit={handleSave} className="grid gap-5 px-6 pb-8 sm:grid-cols-2 sm:px-8">
        <div>
          <Label htmlFor="fullName">Full name</Label>
          <Input
            id="fullName"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            minLength={2}
            maxLength={120}
            placeholder="Your name"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="phone">Phone (private)</Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={30}
            placeholder="Only used by Shields"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            value={username}
            onChange={(e) =>
              setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase())
            }
            maxLength={20}
            placeholder="e.g. sam_leeds"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Email</Label>
          <Input value={data.profile?.email ?? ""} disabled className="mt-1.5" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bio">About you</Label>
          <textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="A line or two — e.g. first-time homeowner in Leeds"
            className="mt-1.5 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50"
          />
        </div>
        <div className="sm:col-span-2">
          <Button
            type="submit"
            size="sm"
            className="min-h-10"
            disabled={saving || fullName.trim().length < 2}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Save profile
          </Button>
        </div>
        </form>
      </details>
    </section>
  );
}

const PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "flat", label: "Flat" },
  { value: "bungalow", label: "Bungalow" },
  { value: "commercial", label: "Commercial" },
  { value: "other", label: "Other" },
];

function PropertiesSection() {
  const { data } = useSuspenseQuery(profileQuery);
  const queryClient = useQueryClient();
  const add = useServerFn(addProperty);
  const remove = useServerFn(deleteProperty);
  const [label, setLabel] = useState("");
  const [propertyType, setPropertyType] = useState("house");
  const [addressLine, setAddressLine] = useState("");
  const [townCity, setTownCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    const res = await add({
      data: {
        label,
        propertyType: propertyType as "house" | "flat" | "bungalow" | "commercial" | "other",
        addressLine: addressLine || null,
        townCity: townCity || null,
        postcode,
      },
    });
    setSaving(false);
    if (!res.ok) {
      toast.error(res.error ?? "Could not save the property");
      return;
    }
    toast.success("Property saved");
    setLabel("");
    setAddressLine("");
    setTownCity("");
    setPostcode("");
    await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
  }

  async function handleDelete(id: string) {
    const res = await remove({ data: { propertyId: id } });
    if (!res.ok) {
      toast.error(res.error ?? "Could not remove the property");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
  }

  return (
    <section className="card-surface mt-6 p-6">
      <h2 className="flex items-center gap-2 font-display font-bold">
        <MapPin className="h-5 w-5 text-primary" aria-hidden /> Saved properties
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Save the places you book for — home, rentals or business premises.
      </p>

      {data.properties.length > 0 && (
        <ul className="mt-4 space-y-2">
          {data.properties.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3"
            >
              <div className="min-w-0">
                <p className="font-semibold">{p.label}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {[p.address_line, p.town_city, p.postcode].filter(Boolean).join(", ")}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(p.id)}
                aria-label={`Remove ${p.label}`}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleAdd} className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="propLabel">Label</Label>
          <Input
            id="propLabel"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={60}
            placeholder="e.g. Home, Mum's flat"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Property type</Label>
          <Select value={propertyType} onValueChange={setPropertyType}>
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="propAddress">Address</Label>
          <Input
            id="propAddress"
            value={addressLine}
            onChange={(e) => setAddressLine(e.target.value)}
            maxLength={200}
            placeholder="Street address"
            className="mt-1.5"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="propTown">Town / city</Label>
            <Input
              id="propTown"
              value={townCity}
              onChange={(e) => setTownCity(e.target.value)}
              maxLength={120}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="propPostcode">Postcode</Label>
            <Input
              id="propPostcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              required
              minLength={3}
              maxLength={10}
              className="mt-1.5"
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" variant="outline" className="min-h-11" disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Add property
          </Button>
        </div>
      </form>
    </section>
  );
}
